// Design tokens — BuyerGram editorial-concierge system (redesign v1).
//
// Color philosophy: cream paper background + warm-ink text + persimmon-red
// accent. Reads as editorial concierge rather than messaging-app dark.
// Source palette (extracted from the Claude-Design hi-fi prototype):
//
//   #F4EFE7  paper       — primary canvas
//   #FBF8F2  paperRaised — cards, inputs
//   #14110D  ink         — primary text, bold UI elements
//   #1a1614  inkRaised   — dark hero / wallet balance card
//   #C8412A  persimmon   — accent, CTA, focus, alerts of urgency
//
// Theme variants pre-validated for future verticalization: persimmon (default),
// bottle-green, ink-navy, ochre. Each swaps `accent` only; all other tokens
// stay the same.
//
// API surface kept stable with the previous T export so all existing
// inline-styled components continue to compile. Renamed semantics:
//   T.bg          → paper canvas (was dark navy)
//   T.sidebar     → slightly raised paper for the side rail
//   T.sidebarActive → ink-on-paper hover/active fill
//   T.card        → paperRaised (modals, forms)
//   T.text        → ink (was off-white)
//   T.textSec     → ink at 56% (cool gray formerly used)
//   T.accent      → persimmon (was Telegram cyan)
//   T.danger      → deeper persimmon for destructive actions
//   T.online      → bottle-green for success states
//   T.border      → cream border (was near-black)
//   T.overlay     → ink at 50% for modal scrim
//
// Plus new tokens exported alongside (T_NEW) — not used by legacy code but
// available to newly-built components for the redesign.

// ─── Core token palette (canonical) ────────────────────────────────
export const TOKENS = {
  // Paper canvas + raised cards
  paper:        "#F4EFE7",
  paperRaised:  "#FBF8F2",
  paperSoft:    "#F0E9DC",      // subtle dividers, hover wash on light surfaces

  // Ink — warm near-black, used for text + bold UI elements
  ink:          "#14110D",
  inkRaised:    "#1a1614",      // dark hero cards (wallet balance, scenario labels)
  ink87:        "rgba(20,17,13,0.87)",
  ink56:        "rgba(20,17,13,0.56)",  // secondary text
  ink38:        "rgba(20,17,13,0.38)",  // tertiary / disabled
  ink12:        "rgba(20,17,13,0.12)",  // borders, dividers
  ink08:        "rgba(20,17,13,0.08)",  // very subtle backgrounds

  // Persimmon — accent (CTA, focus rings, urgency)
  persimmon:    "#C8412A",
  persimmonHover:"#B33A24",
  persimmonSoft:"rgba(200,65,42,0.10)", // attention strips
  persimmonRing:"rgba(200,65,42,0.25)", // focus rings

  // Status tokens — non-accent semantic colors
  bottle:       "#2F5D4A",      // success / online (bottle-green, harmonizes with cream)
  bottleSoft:   "rgba(47,93,74,0.10)",
  ochre:        "#B8860B",      // warning amber (over-customs, pending)
  ochreSoft:    "rgba(184,134,11,0.10)",
  danger:       "#8B2A1C",      // deep crimson, distinct from persimmon accent
  dangerSoft:   "rgba(139,42,28,0.10)",

  // Paper-on-ink (for dark hero cards / pick-list print) — inverted
  paperOnInk:   "#F0E9DC",
  ink56OnInk:   "rgba(240,233,220,0.56)",
};

// ─── Compatibility layer (legacy T export) ─────────────────────────
// Maps the editorial palette onto the names the existing components
// already import (T.bg, T.accent, etc.). Doing this lets every
// inline-styled view that's already in the tree pick up the new look
// without a rewrite — full per-screen redesign happens incrementally.
export const T = {
  // Surfaces (was: dark navy variants)
  bg:            TOKENS.paper,
  sidebar:       TOKENS.paperRaised,
  sidebarHover:  TOKENS.paperSoft,
  sidebarActive: TOKENS.ink08,
  chatBg:        TOKENS.paper,
  card:          TOKENS.paperRaised,
  topicBg:       TOKENS.paperSoft,

  // Bubbles in DM (was: blue mine / dark theirs)
  myMsg:         TOKENS.ink,           // ink-on-paper, RIGHT side
  theirMsg:      TOKENS.paperRaised,   // cream-on-paper, LEFT side

  // Text
  text:          TOKENS.ink,
  textSec:       TOKENS.ink56,

  // Accents + semantic
  accent:        TOKENS.persimmon,
  danger:        TOKENS.danger,
  border:        TOKENS.ink12,
  overlay:       "rgba(20,17,13,0.55)",
  online:        TOKENS.bottle,
};

// ─── New tokens exported separately (for redesign-aware components) ─
export const T_NEW = {
  ...TOKENS,
  // Typography scale (px). The 38 / 26 / 18 / 14 / 12 ladder is from
  // the design's hero serif / display / body / meta / micro hierarchy.
  fontSerif:     '"IBM Plex Serif", Georgia, "Times New Roman", serif',
  fontSans:      '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontMono:      '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, Monaco, "Cascadia Mono", "Roboto Mono", monospace',
  // Type scale
  hero:          38, displayLg: 28, displayMd: 22, displaySm: 18,
  body:          14, bodyLg: 16, bodySm: 13, meta: 12, micro: 11,
  // Semantic aliases referenced throughout the redesign components but
  // never defined on T_NEW — without these, T_NEW.accent / success /
  // overlay resolved to `undefined` and leaked invalid CSS into inline
  // styles (`background: undefined`, `1px solid undefined`), silently
  // breaking active/selected states (lang switch, gender chips, address
  // tabs). Values mirror `T`.
  accent:        TOKENS.persimmon,
  success:       TOKENS.bottle,
  overlay:       "rgba(20,17,13,0.55)",
  // Letter-spacing for UPPERCASE Plex Mono labels (0.04em → ~0.5px @12px)
  monoTrack:     "0.06em",
  // Spacing scale (4px base): 4, 8, 12, 16, 20, 24, 32, 40, 56, 72
  s1: 4, s2: 8, s3: 12, s4: 16, s5: 20, s6: 24, s7: 32, s8: 40, s9: 56, s10: 72,
  // Border radii
  r1: 4, r2: 8, r3: 12, r4: 16, r5: 24, rFull: 9999,
  // Shadows (lifted from the prototype's card-rest-state shadow)
  shadowCard:    "0 1px 2px rgba(20,17,13,0.04), 0 2px 8px rgba(20,17,13,0.06)",
  shadowModal:   "0 8px 28px rgba(20,17,13,0.16), 0 24px 64px rgba(20,17,13,0.18)",
  shadowFocus:   `0 0 0 3px ${TOKENS.persimmonRing}`,
  // Motion (kept conservative; respect prefers-reduced-motion in components)
  durFast:       "120ms", durBase: "180ms", durSlow: "240ms",
  ease:          "cubic-bezier(0.2, 0.0, 0.0, 1.0)",
};

// Stable per-name color picker for avatar fallbacks (initials on hashed bg).
// Updated palette — warmer earthtones to harmonize with cream paper, no
// fluorescent jewel tones from the previous Telegram-themed picker.
export const COLORS = [
  "#C8412A", // persimmon
  "#8B2A1C", // crimson
  "#B8860B", // ochre
  "#2F5D4A", // bottle
  "#7E5D3B", // walnut
  "#4A4035", // espresso
  "#A85643", // terracotta
  "#5B7553", // sage
  "#6B4C8B", // plum
  "#3D5A6C", // slate-navy
];
export const colorFor = (n) =>
  COLORS[Math.abs([...(n || "?")].reduce((a, c) => a + c.charCodeAt(0), 0)) % COLORS.length];
